import React, { useEffect, useRef, useState, useContext } from "react";
import { createChart, CandlestickSeries } from "lightweight-charts";
import { useQuery } from "@tanstack/react-query";
import { Pulse, WaveSine } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { SSEContext } from "@/components/AppShell";

/**
 * MarketChart — TradingView Lightweight Charts candle chart fed by
 * /api/market/ohlc/:symbol. Live tick updates from SSE patch the last bar's
 * close so the chart "breathes" between OHLC refreshes.
 */
export default function MarketChart({ symbol, levels = null }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const lastBarRef = useRef(null);
  const priceLinesRef = useRef([]);
  const [empty, setEmpty] = useState(false);
  const [timeframe, setTimeframe] = useState("1m");
  const { tickMap } = useContext(SSEContext);
  const normalizedSymbol = (symbol || "").toUpperCase();

  const { data, isLoading, error } = useQuery({
    queryKey: ["candles", normalizedSymbol, timeframe],
    enabled: Boolean(normalizedSymbol),
    queryFn: () => api.get(`/market/candles/${normalizedSymbol}`, { params: { limit: 500, timeframe } }).then((r) => r.data),
    refetchInterval: 60_000,
  });

  // Force an immediate refresh when timeframe or symbol changes so the
  // chart updates without waiting for the standard polling/caching window.
  // useEffect(() => {
  //   if (!normalizedSymbol) return;
  //   try { refetch?.(); } catch { /* noop */ }
  // }, [normalizedSymbol, timeframe, refetch]);

  // Build the chart once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: "solid", color: "transparent" },
        textColor: "#A1A1AA",
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.10)" },
      timeScale: {
        borderColor: "rgba(255,255,255,0.10)",
        timeVisible: true,
        secondsVisible: false,
        // Custom formatter — bypasses lightweight-charts v5's default
        // Date.toLocaleString("en-US@posix", ...) which crashes in Chromium.
        tickMarkFormatter: (time) => {
          const d = new Date(time * 1000);
          const hh = String(d.getUTCHours()).padStart(2, "0");
          const mm = String(d.getUTCMinutes()).padStart(2, "0");
          return `${hh}:${mm}`;
        },
      },
      localization: {
        timeFormatter: (time) => {
          const d = new Date(time * 1000);
          return d.toISOString().slice(11, 19) + " UTC";
        },
      },
      crosshair: { mode: 1 },
      autoSize: true,
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#10B981",
      downColor: "#EF4444",
      borderUpColor: "#10B981",
      borderDownColor: "#EF4444",
      wickUpColor: "#10B981",
      wickDownColor: "#EF4444",
    });
    chartRef.current = chart;
    seriesRef.current = series;
    return () => { chart.remove(); chartRef.current = null; seriesRef.current = null; };
  }, []);

  // Push bars when the REST batch updates
  useEffect(() => {
    if (!seriesRef.current || !data?.bars?.length) {
      setEmpty(data?.bars?.length === 0);
      return;
    }
    seriesRef.current.setData(data.bars);
    lastBarRef.current = data.bars[data.bars.length - 1];
    setEmpty(false);
  }, [data]);

  // Patch last bar's close from SSE tick (real or synth) so the candle "breathes"
  // const [dbg, setDbg] = useState({ tickTs: null, bucket: null, lastTime: null, price: null });

  // useEffect(() => {
  //   if (!seriesRef.current || !lastBarRef.current) return;
  //   // Use the normalized symbol (matches REST keying) for SSE lookup
  //   const tick = tickMap[symbol];
  //   if (!tick?.price) return;

  //   const last = lastBarRef.current;
    // Determine tick timestamp in seconds. If the tick carries a time use it,
    // otherwise use now. Then round it down to the timeframe resolution
    // (currently 1m) so that minute-boundary ticks create new bars.
    // let tickTs = (typeof tick.ts !== "undefined") ? Math.floor(Number(tick.ts)) : Math.floor(Date.now() / 1000);
    // Auto-detect ms vs s timestamps: if ts looks like milliseconds, convert to seconds
    // if (tickTs > 1e12) tickTs = Math.floor(tickTs / 1000);
    // For 1m timeframe we bucket to minute; for other timeframes this can be
    // extended later. For now compute the timeframe seconds from `timeframe`.
  //   const tfSeconds = (() => {
  //     if (timeframe === "1m") return 60;
  //     if (timeframe === "5m") return 60 * 5;
  //     if (timeframe === "15m") return 60 * 15;
  //     if (timeframe === "30m") return 60 * 30;
  //     if (timeframe === "1h") return 60 * 60;
  //     if (timeframe === "4h") return 60 * 60 * 4;
  //     if (timeframe === "1d") return 60 * 60 * 24;
  //     return 60;
  //   })();
  //   const bucket = Math.floor(tickTs / tfSeconds) * tfSeconds;

  //   if (bucket === last.time) {
  //     // Update the existing last bar (breathing between REST refreshes)
  //     if (window && window.__MARKETCHART_DEBUG__) {
  //       // eslint-disable-next-line no-console
  //       console.debug("MarketChart: patch existing bar", { symbol: normalizedSymbol, tick, bucket, last });
  //     }
  //     setDbg({ tickTs, bucket, lastTime: last.time, price: tick.price });
  //     const patched = {
  //       time: last.time,
  //       open: last.open,
  //       high: Math.max(last.high, tick.price),
  //       low:  Math.min(last.low,  tick.price),
  //       close: tick.price,
  //     };
  //     lastBarRef.current = patched;
  //     seriesRef.current.update(patched);
  //   } else if (bucket > last.time) {
  //     // New timeframe bucket — create a new bar whose open is previous
  //     // close and whose high/low/close are the tick price.
  //     if (window && window.__MARKETCHART_DEBUG__) {
  //       // eslint-disable-next-line no-console
  //       console.debug("MarketChart: append new bar", { symbol: normalizedSymbol, tick, bucket, last });
  //     }
  //     setDbg({ tickTs, bucket, lastTime: last.time, price: tick.price });
  //     const next = {
  //       time: bucket,
  //       open: last.close,
  //       high: tick.price,
  //       low: tick.price,
  //       close: tick.price,
  //     };
  //     lastBarRef.current = next;
  //     // `update` with a later time will append a new bar in lightweight-charts
  //     seriesRef.current.update(next);
  //   }
  // // Track changes to the specific symbol's tick price/time and timeframe.
  // }, [tickMap, normalizedSymbol, timeframe, tickMap?.[normalizedSymbol]?.price, tickMap?.[normalizedSymbol]?.ts]);

  // // Small on-chart debug overlay for quick visual inspection
  // const DebugOverlay = () => (
  //   <div className="absolute right-3 top-3 bg-black/60 text-xs mono px-2 py-1 rounded text-zinc-300 pointer-events-none z-20">
  //     <div>tick_ts: {dbg.tickTs ?? "—"}</div>
  //     <div>bucket: {dbg.bucket ?? "—"}</div>
  //     <div>last_bar: {dbg.lastTime ?? "—"}</div>
  //     <div>price: {dbg.price ?? "—"}</div>
  //   </div>
  // );
  useEffect(() => {
    if (!seriesRef.current || !lastBarRef.current) return;
    const tick = tickMap[normalizedSymbol];
    if (!tick?.price) return;

    const last = lastBarRef.current;
    const tfSeconds = { "1m":60, "5m":300, "15m":900, "30m":1800, "1h":3600, "4h":14400, "1d":86400 }[timeframe] || 60;
    const tickTs = typeof tick.ts !== "undefined"
      ? (Number(tick.ts) > 1e12 ? Math.floor(Number(tick.ts) / 1000) : Math.floor(Number(tick.ts)))
      : Math.floor(Date.now() / 1000);
    const bucket = Math.floor(tickTs / tfSeconds) * tfSeconds;

    if (bucket === last.time) {
      const patched = { ...last, high: Math.max(last.high, tick.price), low: Math.min(last.low, tick.price), close: tick.price };
      lastBarRef.current = patched;
      seriesRef.current.update(patched);
    } else if (bucket > last.time) {
      const next = { time: bucket, open: last.close, high: tick.price, low: tick.price, close: tick.price };
      lastBarRef.current = next;
      seriesRef.current.update(next); // later time → lightweight-charts appends a new candle
    }
  }, [tickMap, symbol, timeframe]);

  // Draw Entry / SL / TP1 / TP2 horizontal lines from the active setup
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    // Clear previous lines
    priceLinesRef.current.forEach((pl) => {
      try { series.removePriceLine(pl); } catch { /* noop */ }
    });
    priceLinesRef.current = [];

    if (!levels) {
      // Reset autoscale to candle-only when no levels
      try { series.applyOptions({ autoscaleInfoProvider: undefined }); } catch { /* noop */ }
      return;
    }
    const { entry_low, entry_high, sl, t1, t2, bias } = levels;
    const isBull = bias === "BULLISH";
    const lines = [
      { price: entry_low,  title: isBull ? "ENTRY ▲ (zone low)"  : "ENTRY ▼ (zone low)",  color: "#FBBF24", style: 2 },
      { price: entry_high, title: isBull ? "ENTRY ▲ (zone high)" : "ENTRY ▼ (zone high)", color: "#FBBF24", style: 2 },
      { price: sl,         title: "SL ✕",                                                  color: "#EF4444", style: 0 },
      { price: t1,         title: "TP1 · 1:1 R",                                           color: "#10B981", style: 0 },
      { price: t2,         title: "TP2 · 2.5 R",                                           color: "#22D3EE", style: 0 },
    ].filter((l) => typeof l.price === "number" && Number.isFinite(l.price));

    lines.forEach((l) => {
      const pl = series.createPriceLine({
        price: l.price,
        color: l.color,
        lineWidth: 2,
        lineStyle: l.style,   // 0 = Solid, 2 = Dashed
        axisLabelVisible: true,
        title: l.title,
      });
      priceLinesRef.current.push(pl);
    });

    // Expand the chart's autoscale to include all price lines so nothing
    // (TP2 in particular) gets clipped off-screen by candle-only autoscaling.
    const prices = lines.map((l) => l.price);
    const lineMin = Math.min(...prices);
    const lineMax = Math.max(...prices);
    series.applyOptions({
      autoscaleInfoProvider: (orig) => {
        const r = orig();
        if (!r || !r.priceRange) {
          return { priceRange: { minValue: lineMin, maxValue: lineMax } };
        }
        const pad = (lineMax - lineMin) * 0.05;   // 5 % visual padding
        return {
          ...r,
          priceRange: {
            minValue: Math.min(r.priceRange.minValue, lineMin) - pad,
            maxValue: Math.max(r.priceRange.maxValue, lineMax) + pad,
          },
        };
      },
    });
  }, [levels]);

  return (
    <div className="surface relative" data-testid="market-chart">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <WaveSine size={16} className="text-gold" />
            <span className="overline">Live Chart · {symbol}</span>
          </div>
          <div className="flex bg-black/40 border border-white/10 rounded overflow-hidden">
            {["1m", "5m", "15m", "30m", "1h", "4h", "1d"].map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}                className={`px-2 py-1 text-[10px] mono uppercase tracking-wider transition ${
                  timeframe === tf ? "bg-gold text-black font-bold" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.live && <span className="chip chip-bull"><Pulse size={11} weight="fill" /> LIVE</span>}
          {data && !data.live && <span className="chip chip-neutral">SYNTH</span>}
          {data?.fetched_at && <span className="mono text-[10px] text-zinc-500">refreshed {new Date(data.fetched_at).toLocaleTimeString()}</span>}
        </div>
      </div>
      <div ref={containerRef} className="h-[360px] w-full" />
      
      {error && (
        <div className="absolute inset-0 flex items-center justify-center text-bear mono text-xs px-4 text-center">
          Candles request failed: {error.message || "check backend connectivity"}
        </div>
      )}
      {(isLoading || empty) && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-zinc-500 mono text-xs">
          {isLoading ? "Loading bars…" : "No OHLC data yet — fetch will populate within 60s."}
        </div>
      )}
    </div>
  );
}

